// @ts-strict-ignore
import Head from 'next/head';
import { createGlobalStyle } from 'styled-components';
import reset from 'styled-reset';

import Script from 'next/script';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import * as fonts from '../src/index.css';
import AppProviders from '../src/AppProviders';
import { SetFAQStructuredData } from '../src/components/Marketing/FAQ/StructuredFAQ';

const GlobalStyle = createGlobalStyle`
    ${reset}
    ${fonts}

    body {
      background: #e4ecf0;
      overflow: unset;
    }
`;

const gtm = process.env.CARVANA_APP_GTM_ID;

function MyApp({ Component, pageProps, cookies }: AppProps & { cookies: string }) {
  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=5,shrink-to-fit=no"
        />
        <meta
          name="description"
          data-cv-test="valueTrackerSeoDescription"
          content="Track your car’s value with Carvana Value Tracker and get automatic, personal updates on your car's worth. Share your vehicle details to start now!"
        />
        <meta name="theme-color" content="#00619e" />
        <title>Carvana | Track Your Car’s Value Online </title>
        <link rel="canonical" href="https://www.carvana.com/value-tracker" />
        <link
          rel="manifest"
          href={`${process.env.CARVANA_APP_STC_SHELL_PUBLIC_URL}/manifest.json`}
        />
        <link
          rel="shortcut icon"
          href={`${process.env.CARVANA_APP_STC_SHELL_PUBLIC_URL}/favicon.ico`}
        />

        <link
          rel="preload"
          href="https://assets.fastly.carvana.io/fonts/BrandonText-Light.woff2"
          crossOrigin=""
          as="font"
          type="font/woff2"
        />
        <link
          rel="preload"
          href="https://assets.fastly.carvana.io/fonts/BrandonText-Bold.woff2"
          crossOrigin=""
          as="font"
          type="font/woff2"
        />
        <link
          rel="preload"
          href="https://assets.fastly.carvana.io/fonts/BrandonText-Regular.woff2"
          crossOrigin=""
          as="font"
          type="font/woff2"
        />
      </Head>
      <Script
        id="structure-data"
        key="structured-data"
        type="application/ld+json"
        // ref from https://nextjs.org/learn/seo/rendering-and-ranking/metadata - Structured Data and Json-LD section
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={SetFAQStructuredData()}
      />
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        // eslint-disable-next-line react/no-danger -- entities will be created unless dangerously set
        dangerouslySetInnerHTML={{
          __html: `
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
            });
            var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src =
                'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${gtm}');
          `
        }}
      />
      <GlobalStyle />
      <AppProviders pageProps={pageProps} cookies={cookies}>
        <Component {...pageProps} />
      </AppProviders>
    </>
  );
}

MyApp.getInitialProps = async (
  context: AppContext
): Promise<AppInitialProps & { cookies: string }> => {
  const ctx = await App.getInitialProps(context);
  const cookies = context.ctx.req?.headers?.cookie;

  return { ...ctx, cookies };
};

export default MyApp;
